// src/components/PatientDetails/sections/UnifiedCaptureHub/ChatInterface.tsx
/**
 * Chat-based interface for patient data capture
 * Cursor-style design with voice transcription editing
 */

import {
  alpha,
  Box,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { fetchData, uploadData } from "api/apiHelpers";
import ChatHistory from "components/chat/ChatHistory";
import { ChatMessageType } from "components/chat/ChatMessage";
import ExpandableDocumentList, {
  SubmittedDocument,
} from "components/chat/ExpandableDocumentList";
import PatientChatInput from "components/chat/PatientChatInput";
import RecordingControls from "components/chat/RecordingControls";
import CameraModal from "components/layout/modals/CameraModal";
import DocumentModal from "components/layout/modals/DocumentModal";
import { NeumorphicCard } from "components/layout/NeumorphicCard";
import { useAuth } from "hooks/useAuth";
import { useCamera } from "hooks/useCamera";
import {
  cleanupOldSessions,
  deleteSession,
  finalizeSession,
  getOrphanedSessions,
  getPendingChunks,
} from "hooks/useIndexedDBBackup";
import { useNotification } from "hooks/useNotification";
import { useTutorial } from "hooks/useOnboardingTutorial";
import { useUploadQueue } from "hooks/useUploadQueue";
import { useVoiceRecorder } from "hooks/useVoiceRecorder";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import logger from "utils/logger";
import { useTranslation, useLanguageSwitcher } from "../../../../i18n";
import { speechLocalesFor, warnIfNonEnglish } from "../../../../i18n/speechLocale";

/** Response from the patient-documents upload endpoint */
interface DocumentUploadResponse {
  id?: string | number;
  file_url?: string;
  file?: string;
}

/** Common shape for documents that can be synced to the chat timeline */
interface SyncableDocument {
  id: string;
  file_name?: string;
  file_url?: string;
  file?: string;
  text?: string;
  created_at?: string;
  processing_status?: string;
}

interface ChatInterfaceProps {
  submittedDocuments: SubmittedDocument[]; // Filtered docs for dropdown (visualizable files only)
  allDocuments?: SyncableDocument[]; // ALL docs for syncing to chat (including text-only)
  onDocumentDelete?: (id: string) => void;
  onSubmit: () => void;
  isExtracting?: boolean; // True when tables are being extracted in the background
  domainSelector?: React.ReactNode; // Optional domain selector to render in header
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  submittedDocuments,
  allDocuments,
  onDocumentDelete,
  onSubmit,
  isExtracting = false,
  domainSelector,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { language } = useLanguageSwitcher();
  const { id: patientId } = useParams<{ id: string }>();
  const notification = useNotification();
  const { user } = useAuth();
  const { isActive: tutorialActive } = useTutorial();

  // Get current user display name
  const currentUserName = user
    ? user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`.trim()
      : user.username
    : t("chat.user_fallback", "User");

  // localStorage key for chat messages
  const STORAGE_KEY = `chat_messages_${patientId}`;

  // Load messages from localStorage on mount
  const loadMessages = (): ChatMessageType[] => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convert timestamp strings back to Date objects
        return parsed.map((m: ChatMessageType & { sender?: string }) => ({
          ...m,
          timestamp: new Date(m.timestamp),
          sender: m.sender || undefined, // Handle older messages without sender
        }));
      }
    } catch (e) {
      logger.error("[Chat] Failed to load messages from localStorage:", e);
    }
    return [];
  };

  // State
  const [messages, setMessages] = useState<ChatMessageType[]>(loadMessages);
  const [inputValue, setInputValue] = useState("");
  const [processing, setProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [documentModalUrls, setDocumentModalUrls] = useState<string[]>([]);
  // Parallel to documentModalUrls: the export-scope-gated download URL + filename for each
  // doc, so the modal's "Download" button routes through the gated endpoint (066 B / option A).
  const [documentModalDownloadUrls, setDocumentModalDownloadUrls] = useState<
    (string | undefined)[]
  >([]);
  const [documentModalFileNames, setDocumentModalFileNames] = useState<
    (string | undefined)[]
  >([]);
  const [documentModalIndex, setDocumentModalIndex] = useState<number | null>(
    null
  );

  // Persist messages to localStorage whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
      } catch (e) {
        logger.error("[Chat] Failed to save messages to localStorage:", e);
      }
    }
  }, [messages, STORAGE_KEY]);

  // Track which document IDs we've already added to chat
  const syncedDocIdsRef = useRef<Set<string>>(new Set());

  // Sync ALL documents into chat as messages (for documents uploaded at patient creation or in past sessions)
  // Uses allDocuments (if provided) which includes text-only messages
  // Falls back to submittedDocuments for backward compatibility
  useEffect(() => {
    const docsToSync = allDocuments || submittedDocuments;
    if (!docsToSync || docsToSync.length === 0) return;

    setMessages((currentMessages) => {
      const newMessages: ChatMessageType[] = [];
      // Mutable copy so we can upgrade optimistic bubbles in place (e.g. a voice
      // note whose blob: URL is dead after reload) instead of skipping them.
      const working = [...currentMessages];

      for (const doc of docsToSync) {
        // Skip if already synced in this render cycle
        if (syncedDocIdsRef.current.has(String(doc.id))) continue;

        // Check if this document already exists in current chat messages
        // Use normalized string comparison to handle number vs string ID mismatches
        const docIdStr = String(doc.id);
        const syncDoc = doc as SyncableDocument;
        const textContent = syncDoc.text || "";
        const docFileName = syncDoc.file_name || "";
        const fileUrl = syncDoc.file_url || syncDoc.file || "";
        const matchIdx = working.findIndex(
          (m) =>
            String(m.metadata?.serverId) === docIdStr ||
            // Fallback: match text messages by content for optimistically added messages that lack serverId
            (m.type === "text" && !m.metadata?.serverId && m.content === textContent && textContent) ||
            // Fallback: match file messages by fileName for optimistically added messages that lack serverId
            (m.type !== "text" && !m.metadata?.serverId && docFileName && m.metadata?.fileName === docFileName)
        );

        if (matchIdx >= 0) {
          // Already in the timeline (optimistic or prior sync). Reconcile it with
          // the persisted document: attach the serverId, and for file/voice/photo
          // bubbles swap the dead local blob: URL for the durable server file_url.
          const existing = working[matchIdx];
          working[matchIdx] = {
            ...existing,
            content:
              existing.type !== "text" && fileUrl ? fileUrl : existing.content,
            metadata: { ...existing.metadata, serverId: doc.id },
          };
        } else {
          const fileName = syncDoc.file_name || "";
          const createdAt = syncDoc.created_at || new Date().toISOString();

          // Check if this is a text-only message (no file, only text)
          const isTextOnly = textContent && !fileUrl;

          if (isTextOnly) {
            // Sync as text message
            newMessages.push({
              id: `doc-${doc.id}`,
              type: "text",
              content: textContent,
              timestamp: new Date(createdAt),
              sender: undefined, // From past session
              metadata: {
                serverId: doc.id,
              },
            });
          } else if (fileUrl) {
            // Determine the appropriate message type based on the file
            const lowerFileName = fileName.toLowerCase();
            const lowerUrl = fileUrl.toLowerCase();

            // Check if it's an image
            const isImage =
              /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(lowerFileName) ||
              /\.(jpg|jpeg|png|gif|webp|bmp|svg)/i.test(lowerUrl) ||
              lowerUrl.includes("image/");

            // Check if it's audio (voice recording)
            const isAudio =
              /\.(mp3|wav|webm|ogg|m4a)$/i.test(lowerFileName) ||
              /\.(mp3|wav|webm|ogg|m4a)/i.test(lowerUrl) ||
              lowerUrl.includes("audio/");

            // Determine message type
            let messageType: ChatMessageType["type"] = "document";
            if (isImage) {
              messageType = "photo";
            } else if (isAudio) {
              messageType = "voice";
            }

            // For voice notes, include transcription from the text field
            const voiceMetadata: ChatMessageType["metadata"] = {
              fileName: fileName,
              serverId: doc.id,
            };

            // If it's a voice note and has text content, that's the transcription
            if (isAudio && textContent) {
              voiceMetadata.transcription = textContent;
            }

            newMessages.push({
              id: `doc-${doc.id}`,
              type: messageType,
              content: fileUrl,
              timestamp: new Date(createdAt),
              sender: undefined, // System/initial upload
              metadata: voiceMetadata,
            });
          }
        }
        // Mark as synced regardless
        syncedDocIdsRef.current.add(String(doc.id));
      }

      if (newMessages.length > 0) {
        logger.debug(
          `[Chat] Syncing ${newMessages.length} documents from server to chat`
        );
        // Re-sort the WHOLE combined list (server-fetched + existing
        // localStorage-restored messages) ascending by timestamp so the
        // chat reads chronological — oldest at top, newest at bottom,
        // input at the bottom. The previous `newMessages.sort() + prepend`
        // produced inconsistent order when an older Document arrived after
        // newer localStorage messages were already in the working array.
        // See cursor_logs/036 §16.
        const combined = [...working, ...newMessages];
        combined.sort(
          (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
        );
        return combined;
      }

      return working;
    });
  }, [allDocuments, submittedDocuments]);

  // Voice transcription editing state
  const [pendingVoiceData, setPendingVoiceData] = useState<{
    audioUrl: string;
    duration: number;
    transcription: string; // Store transcription here too
    sessionId: string; // recording session — needed for phase-2 finalize on send
    expectedChunks?: number;
  } | null>(null);

  // Store original input value before recording starts
  const inputBeforeRecordingRef = useRef<string>("");

  // Hooks
  const { videoRef, startCamera, stopCamera, capturePhoto } = useCamera();
  const recoveryUploadQueue = useUploadQueue();

  // Ref for voice blob URL
  const voiceBlobUrlRef = useRef<string | null>(null);

  // Latest UI language, read inside the async recorder callback (avoids a stale
  // closure) to drive the transcription locale candidates.
  const langRef = useRef(language);
  langRef.current = language;

  // Effect to update input when transcription arrives
  useEffect(() => {
    if (pendingVoiceData && pendingVoiceData.transcription) {
      const transcription = pendingVoiceData.transcription;
      const existingText = inputBeforeRecordingRef.current;
      const newText = existingText
        ? `${existingText}\n\n${transcription}`
        : transcription;

      logger.debug("[Voice] useEffect setting inputValue to:", newText);
      setInputValue(newText);
      inputBeforeRecordingRef.current = "";
    }
  }, [pendingVoiceData?.transcription]); // Triggered when transcription in pendingVoiceData changes

  // Voice recorder with transcription
  const {
    recording,
    amplitude,
    recordingDuration,
    startRecording,
    stopRecording,
    cancelRecording,
  } = useVoiceRecorder(async (blob, url, name) => {
    setProcessing(true);
    setProcessingMessage(t("chat.transcribing", "Transcribing audio..."));

    // Store the audio URL for later
    voiceBlobUrlRef.current = url;

    const sessionIdMatch = name.match(/voice_([a-f0-9-]+)_/);
    if (!sessionIdMatch) {
      logger.error("[Voice] Could not extract session ID:", name);
      setProcessing(false);
      return;
    }

    const sessionId = sessionIdMatch[1];
    const chunksMatch = name.match(/_(\d+)chunks/);
    const expectedChunks = chunksMatch ? parseInt(chunksMatch[1], 10) : undefined;

    try {
      // Phase 1 (review): transcribe only — NO Document, NO extraction (the backend
      // defaults create_document=false). The doctor edits, then handleSend runs
      // phase 2 with the approved text. expected_chunks lets the backend flag a
      // truncated upload. See cursor_logs/035 B1/B3.
      const response = (await uploadData(
        "/recording/finalize/",
        {
          session_id: sessionId,
          patient_id: patientId,
          expected_chunks: expectedChunks,
          locales: speechLocalesFor(langRef.current),
        },
        "application/json"
      )) as { task_id: string; chunk_warning?: string | null };

      if (response.chunk_warning) {
        notification.warning(response.chunk_warning);
      }

      const taskId = response.task_id;
      let attempts = 0;
      const maxAttempts = 60;

      while (attempts < maxAttempts) {
        await new Promise((r) => setTimeout(r, 3000));

        const statusResp = (await fetchData(
          `/recording/finalize/${taskId}/`
        )) as {
          status: "pending" | "processing" | "completed" | "error";
          transcript?: string;
          formatted?: {
            formatted_text: string;
            is_dialogue: boolean;
            speakers: string[] | null;
          };
          duration_seconds?: number;
          audio_file?: string;
          session_id?: string;
          language?: string | null;
          error?: string;
        };

        if (statusResp.status === "completed") {
          logger.debug("[Voice] Transcription completed");

          // Backend response structure: { status, transcript, formatted: { formatted_text }, ... }
          // NO .result field - data is at top level!
          const transcription =
            statusResp.formatted?.formatted_text || statusResp.transcript || "";
          const duration = statusResp.duration_seconds || 0;

          logger.debug("[Voice] Extracted transcription:", transcription);

          if (!transcription) {
            logger.error("[Voice] ERROR: Transcription is empty!");
            notification.error(
              t("chat.transcription_failed_no_text", "Transcription failed — no text received from backend")
            );
            setProcessing(false);
            setProcessingMessage("");
            return;
          }

          // Set processing to false first
          setProcessing(false);
          setProcessingMessage("");

          // Set pending voice data - this triggers the useEffect that updates inputValue
          setPendingVoiceData({ audioUrl: url, duration, transcription, sessionId, expectedChunks });
          warnIfNonEnglish(statusResp.language, notification, t);

          logger.debug("[Voice] Input will update via useEffect");
          return;
        } else if (statusResp.status === "error") {
          throw new Error(statusResp.error || "Transcription failed");
        }

        attempts++;
      }

      throw new Error("Transcription timeout");
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : String(error);
      logger.error("[Voice] Error:", error);
      notification.error(t("chat.transcription_failed", "Transcription failed: {{error}}").replace("{{error}}", errMsg));
      setProcessing(false);
      setProcessingMessage("");
    }
  }, patientId);

  // Check for orphaned recordings on mount
  useEffect(() => {
    const checkOrphanedRecordings = async () => {
      try {
        await cleanupOldSessions();
        const orphaned = await getOrphanedSessions();

        if (orphaned.length > 0) {
          const session = orphaned[0];

          // Render a clearer prompt — the previous one read
          // "from 01/06/2026, 01:11:38" which looked like "1h 11m of audio".
          // Now: a real date, a relative "X ago", and the estimated audio
          // length from the chunks left in IndexedDB.
          const pendingChunks = await getPendingChunks(session.sessionId);
          const dateStr = new Date(session.startTime).toLocaleString(undefined, {
            day: "numeric",
            month: "long",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
          const ageMs = Date.now() - session.startTime;
          const mins = Math.round(ageMs / 60_000);
          const hrs = Math.round(ageMs / 3_600_000);
          const days = Math.round(ageMs / 86_400_000);
          const agoText =
            mins < 60
              ? t("chat.minutes_ago", "{{n}} minutes ago").replace("{{n}}", String(mins))
              : hrs < 24
                ? t("chat.hours_ago", "{{n}} hours ago").replace("{{n}}", String(hrs))
                : t("chat.days_ago", "{{n}} days ago").replace("{{n}}", String(days));
          // Recorder writes 30-second chunks; estimate is a lower bound (the
          // final chunk is usually shorter, but we round up to the nearest
          // minute so the user gets a useful order-of-magnitude figure).
          const lengthMin = Math.max(1, Math.round((pendingChunks.length * 30) / 60));
          const lengthText =
            lengthMin === 1
              ? t("chat.recover_length_one", "about 1 minute of audio")
              : t("chat.recover_length_n", "about {{n}} minutes of audio").replace(
                  "{{n}}",
                  String(lengthMin),
                );

          const shouldRecover = window.confirm(
            t(
              "chat.recover_confirm",
              "You have an unfinished recording started on {{date}} ({{ago}}), with {{length}}.\n\nRecover and transcribe it?",
            )
              .replace("{{date}}", dateStr)
              .replace("{{ago}}", agoText)
              .replace("{{length}}", lengthText),
          );

          if (shouldRecover) {
            setProcessing(true);
            setProcessingMessage(t("chat.recovering_recording", "Recovering recording..."));

            const pendingChunks = await getPendingChunks(session.sessionId);
            if (pendingChunks.length > 0) {
              await recoveryUploadQueue.uploadPendingChunks(pendingChunks);
            }

            await uploadData(
              "/recording/finalize/",
              {
                session_id: session.sessionId,
                patient_id: session.patientId || patientId,
              },
              "application/json"
            );

            // Mark session as finalized
            await finalizeSession(session.sessionId);

            setProcessing(false);
            setProcessingMessage("");
            notification.success(t("chat.recording_recovered", "Recording recovered successfully!"));

            // Reload to refresh submitted documents
            setTimeout(() => window.location.reload(), 1000);
          } else {
            // User cancelled - remove the orphaned session
            await deleteSession(session.sessionId);
            logger.debug("[Recovery] User cancelled, session deleted");
          }
        }
      } catch (error) {
        logger.error("[Recovery] Error:", error);
        setProcessing(false);
        setProcessingMessage("");
      }
    };

    checkOrphanedRecordings();
  }, [patientId]);

  // Add message to chat
  const addMessage = (
    partial: Omit<ChatMessageType, "id" | "timestamp" | "sender">
  ) => {
    setMessages((prev) => [
      ...prev,
      {
        ...partial,
        id: Date.now().toString(),
        timestamp: new Date(),
        sender: currentUserName,
      },
    ]);
  };

  // Handle sending message (text or voice transcription)
  const handleSend = async () => {
    const textToSend = inputValue.trim();

    if (pendingVoiceData) {
      // Use the edited input if the doctor changed it, else the original transcript.
      const finalTranscription = textToSend || pendingVoiceData.transcription;
      if (!finalTranscription) return;

      const vd = pendingVoiceData;
      // Optimistic chat bubble (audio + the approved transcription). Tag it with
      // the deterministic server filename (recording.py creates the Document as
      // voice_<session>.webm) so the reload sync can recognise this same note,
      // dedup it, and upgrade its dead blob: URL to the persisted file_url.
      addMessage({
        type: "voice",
        content: vd.audioUrl,
        metadata: {
          transcription: finalTranscription,
          duration: vd.duration,
          fileName: `voice_${vd.sessionId}.webm`,
        },
      });
      setPendingVoiceData(null);
      setInputValue("");

      // Phase 2 (send): persist the Document with the APPROVED text + audio and
      // trigger extraction. The backend skips re-transcription because final_text
      // is provided, so extraction NEVER runs on un-approved text (cursor_logs/035 B1).
      try {
        setProcessing(true);
        setProcessingMessage(t("chat.saving_message", "Saving message..."));
        await uploadData(
          "/recording/finalize/",
          {
            session_id: vd.sessionId,
            patient_id: patientId,
            create_document: true,
            final_text: finalTranscription,
            expected_chunks: vd.expectedChunks,
          },
          "application/json"
        );
        setProcessing(false);
        setProcessingMessage("");
        notification.success(
          t("chat.message_saved", "Message saved and processing started")
        );
        // Trigger table generation if domains are selected (onSubmit checks internally)
        onSubmit();
      } catch (error) {
        logger.error("[Voice] Phase-2 finalize (persist) failed:", error);
        notification.error(t("chat.save_failed", "Failed to save message"));
        setProcessing(false);
        setProcessingMessage("");
      }
    } else {
      // Send as text message AND create document on backend
      if (!textToSend) return;

      // Add message to chat UI immediately (optimistic)
      const optimisticId = Date.now().toString();
      setMessages((prev) => [
        ...prev,
        {
          id: optimisticId,
          type: "text" as const,
          content: textToSend,
          timestamp: new Date(),
          sender: currentUserName,
        },
      ]);
      setInputValue("");

      // During the tutorial the seed endpoint populates data directly.
      // Skip the real document upload to avoid triggering the backend
      // extraction pipeline which would leave "Extracting data" stuck.
      if (tutorialActive) {
        onSubmit();
        return;
      }

      // Send text to backend as a document to trigger extraction
      try {
        setProcessing(true);
        setProcessingMessage(t("chat.saving_message", "Saving message..."));

        const response = (await uploadData(
          "/patient-documents/",
          {
            text: textToSend,
            patient: patientId,
          },
          "application/json"
        )) as DocumentUploadResponse;

        // Patch the optimistic message with the server ID so the sync effect won't duplicate it
        if (response?.id) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === optimisticId
                ? { ...m, metadata: { ...m.metadata, serverId: response.id } }
                : m
            )
          );
          // Also mark it in the syncedDocIds set
          syncedDocIdsRef.current.add(String(response.id));
        }

        setProcessing(false);
        setProcessingMessage("");

        // Trigger table generation/update
        onSubmit();

        notification.success(t("chat.message_saved", "Message saved and processing started"));
      } catch (error) {
        logger.error("[Chat] Failed to save text message:", error);
        notification.error(t("chat.save_failed", "Failed to save message"));
        setProcessing(false);
        setProcessingMessage("");
      }
    }
  };

  // Handle file upload with progress
  const handleFileUpload = async (files: File[]) => {
    setProcessing(true);
    const totalFiles = files.length;
    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setProcessingMessage(t("chat.upload_progress", "Uploading {{current}} of {{total}}: {{name}}").replace("{{current}}", String(i + 1)).replace("{{total}}", String(totalFiles)).replace("{{name}}", file.name));

      const formData = new FormData();
      formData.append("file", file);
      formData.append("patient", patientId || "");

      try {
        const rawResponse = await uploadData(
          "/patient-documents/",
          formData
        );
        // Backend returns a list even for single-file uploads; extract first item
        const response = (
          Array.isArray(rawResponse) ? rawResponse[0] : rawResponse
        ) as DocumentUploadResponse;

        // Use server URL from response, fallback to blob URL for immediate display
        const serverUrl = response?.file_url || response?.file || "";
        const fileUrl = serverUrl || URL.createObjectURL(file);
        const fileType = file.type.startsWith("image/") ? "photo" : "document";

        addMessage({
          type: fileType,
          content: fileUrl,
          metadata: {
            fileName: file.name,
            fileSize: file.size,
            serverId: response?.id,
          },
        });
        successCount++;
      } catch (error) {
        logger.error("Upload failed:", error);
        notification.error(t("chat.upload_failed", "Failed to upload {{name}}").replace("{{name}}", file.name));
        failCount++;
      }
    }

    setProcessing(false);
    setProcessingMessage("");

    // Show summary notification
    if (successCount > 0 && failCount === 0) {
      notification.success(t("chat.upload_success", "{{count}} file(s) uploaded successfully").replace("{{count}}", String(successCount)));
    } else if (successCount > 0 && failCount > 0) {
      notification.warning(t("chat.upload_partial", "{{success}} uploaded, {{failed}} failed").replace("{{success}}", String(successCount)).replace("{{failed}}", String(failCount)));
    }

    onSubmit();
  };

  // Handle camera capture
  const handleCameraCapture = () => {
    setCameraOpen(true);
  };

  const handlePhotoCapture = async (blob: Blob, url: string) => {
    setCameraOpen(false);
    stopCamera();

    setProcessing(true);
    setProcessingMessage(t("chat.uploading_photo", "Uploading photo..."));

    const formData = new FormData();
    formData.append("file", blob, "photo.png");
    formData.append("patient", patientId || "");

    try {
      const rawResponse = await uploadData(
        "/patient-documents/",
        formData
      );
      // Backend returns a list even for single-file uploads; extract first item
      const response = (
        Array.isArray(rawResponse) ? rawResponse[0] : rawResponse
      ) as DocumentUploadResponse;

      // Use server URL from response, fallback to local blob URL
      const serverUrl = response?.file_url || response?.file || "";
      const photoUrl = serverUrl || url;

      addMessage({
        type: "photo",
        content: photoUrl,
        metadata: {
          fileName: "photo.png",
          fileSize: blob.size,
          serverId: response?.id,
        },
      });

      onSubmit();
    } catch (error) {
      logger.error("Photo upload failed:", error);
      notification.error(t("chat.photo_upload_failed", "Failed to upload photo"));
    }

    setProcessing(false);
    setProcessingMessage("");
  };

  // Handle document click. `downloadUrl` (when supplied by the document list) is the
  // export-scope-gated endpoint the modal's Download button uses; viewing uses `url`.
  const handleDocumentClick = (
    url: string,
    downloadUrl?: string,
    fileName?: string
  ) => {
    setDocumentModalUrls([url]);
    setDocumentModalDownloadUrls([downloadUrl]);
    setDocumentModalFileNames([fileName]);
    setDocumentModalIndex(0);
    setDocumentModalOpen(true);
  };

  // Handle voice cancel while editing
  const handleCancelVoice = () => {
    setPendingVoiceData(null);
    // Restore original text if any
    setInputValue(inputBeforeRecordingRef.current);
    inputBeforeRecordingRef.current = "";
  };

  // Handle voice recording start
  const handleVoiceRecordStart = () => {
    if (recording) {
      // Stopping recording
      stopRecording();
    } else {
      // Starting recording - save current input
      inputBeforeRecordingRef.current = inputValue;
      startRecording();
    }
  };

  return (
    <NeumorphicCard
      variant="glass"
      glassOpacityFactor={0.7}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        p: 2,
        boxShadow: "none",
        borderRadius: 3,
        backgroundColor: alpha(theme.palette.secondary.light, 0.5),
        backdropFilter: "blur(8px)",
      }}
    >
      {/* Header Row: Submitted Documents + Domain Selector */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 1,
          flexWrap: "wrap",
        }}
      >
        {/* Left: Submitted Documents */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ExpandableDocumentList
            documents={submittedDocuments}
            onDocumentClick={handleDocumentClick}
            onDocumentDelete={onDocumentDelete}
          />
        </Box>

        {/* Right: Domain Selector */}
        {domainSelector && (
          <Box sx={{ flexShrink: 0, minWidth: 160, maxWidth: 240 }}>
            {domainSelector}
          </Box>
        )}
      </Box>

      {/* Chat History with gradient blend at bottom */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          position: "relative",
          overflow: "hidden", // Container clips, inner scrolls
        }}
      >
        {/* Scrollable chat area */}
        <Box
          sx={{
            height: "100%",
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ChatHistory
            messages={messages}
            onDocumentClick={handleDocumentClick}
          />
        </Box>
      </Box>

      {/* Processing Status Bar — compact indicator above input */}
      {processing && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            py: 1.5,
            px: 3,
            borderTop: `1px solid ${theme.palette.divider}`,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.action.hover,
          }}
        >
          <CircularProgress size={18} thickness={4} />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 500 }}
          >
            {processingMessage || t("chat.processing", "Processing...")}
          </Typography>
        </Box>
      )}

      {/* Extraction Status Indicator */}
      {isExtracting && !processing && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            py: 1.5,
            px: 3,
            borderTop: `1px solid ${theme.palette.divider}`,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: alpha(theme.palette.info.main, 0.06),
          }}
        >
          <CircularProgress size={18} thickness={4} color="info" />
          <Typography
            variant="body2"
            color="info.main"
            sx={{ fontWeight: 500 }}
          >
            {t("chat.extracting", "Extracting clinical data from documents...")}
          </Typography>
        </Box>
      )}

      {/* Recording Bar */}
      {recording && (
        <Box
          sx={{ px: 2, py: 1, borderTop: `1px solid ${theme.palette.divider}` }}
        >
          <RecordingControls
            amplitude={amplitude}
            duration={recordingDuration}
            onDiscard={cancelRecording}
            onStop={stopRecording}
          />
        </Box>
      )}

      {/* Pending Voice Note Indicator */}
      {pendingVoiceData && !recording && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2.5,
            py: 1.25,
            backgroundColor: alpha(theme.palette.info.main, 0.07),
            borderTop: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: theme.palette.info.main,
                animation: "pulse 2s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.3 },
                },
              }}
            />
            <Typography
              variant="body2"
              color="info.main"
              sx={{ fontWeight: 500 }}
            >
              {t("chat.voice_hint", "Voice note transcribed — Edit and press Enter to send")}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            color="error"
            sx={{
              cursor: "pointer",
              fontWeight: 500,
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={handleCancelVoice}
          >
            {t("chat.cancel", "Cancel")}
          </Typography>
        </Box>
      )}

      {/* Chat Input */}
      <PatientChatInput
        value={inputValue || (pendingVoiceData?.transcription ?? "")}
        onChange={setInputValue}
        onSend={handleSend}
        onFileUpload={handleFileUpload}
        onVoiceRecord={handleVoiceRecordStart}
        onCameraCapture={handleCameraCapture}
        disabled={processing}
        recording={recording}
        processing={processing}
        processingMessage={processingMessage}
        placeholder={
          pendingVoiceData
            ? t(
                "chat.edit_transcription",
                "Edit transcription and press Enter to send..."
              )
            : t(
                "chat.type_message",
                "Type a message, upload files, or record a voice note..."
              )
        }
      />

      {/* Camera Modal */}
      <CameraModal
        open={cameraOpen}
        onClose={() => {
          setCameraOpen(false);
          stopCamera();
        }}
        videoRef={videoRef}
        onStart={startCamera}
        onCapture={() => {
          capturePhoto((blob, url) => {
            handlePhotoCapture(blob, url);
          });
        }}
      />

      {/* Document Preview Modal */}
      {documentModalOpen && (
        <DocumentModal
          docs={documentModalUrls}
          downloadUrls={documentModalDownloadUrls}
          fileNames={documentModalFileNames}
          index={documentModalIndex}
          onClose={() => {
            setDocumentModalOpen(false);
            setDocumentModalIndex(null);
          }}
          onNavigate={(newIdx) => setDocumentModalIndex(newIdx)}
        />
      )}
    </NeumorphicCard>
  );
};

export default ChatInterface;
